import { otsu } from "./threshold";
import type { BrickType, Pixels, Rect } from "./types";

export const HALF_BRICK_RATIO = 0.7;
export const MARGIN_MM = 2;
const SPLIT_RATIO = 1.3;
const SPLIT_EDGE_RATIO = 0.35;
const MIN_WIDTH_RATIO = 0.25;
const COURSE_INSET = 0.15;
// Brick rows must vary at least this much more than joint rows; below it the photo has
// no horizontal joints and thresholding would invent courses from noise.
const MIN_ROW_CONTRAST = 1.8;
const JOINT_SPREAD_PERCENTILE = 0.9;
const JOINT_TOLERANCE_FACTOR = 1.5;
const MIN_JOINT_TOLERANCE = 10;
const JOINT_COLUMN_FILL = 0.5;
const MAX_JOINT_SAMPLES = 200_000;

export interface DetectionInput {
  image: Pixels;
  brickWidthMm: number;
  brickHeightMm: number;
}

export interface DetectedRegion extends Rect {
  type: BrickType;
}

export interface DetectionResult {
  pxPerMm: number | null;
  regions: DetectedRegion[];
}

type Colour = [number, number, number];

interface Span {
  start: number;
  end: number;
}

const EMPTY: DetectionResult = { pxPerMm: null, regions: [] };

const isOpaque = (data: Uint8ClampedArray, i: number) => data[i * 4 + 3] >= 128;

const distance = (data: Uint8ClampedArray, i: number, c: Colour) =>
  Math.hypot(data[i * 4] - c[0], data[i * 4 + 1] - c[1], data[i * 4 + 2] - c[2]);

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted.length ? sorted[Math.floor(sorted.length / 2)] : 0;
};

const mean = (values: number[]) => values.reduce((sum, v) => sum + v, 0) / values.length;

const finiteValues = (profile: Float32Array) => Array.from(profile).filter((v) => !Number.isNaN(v));

const medianColour = (data: Uint8ClampedArray, indices: number[]): Colour => {
  const colour: Colour = [0, 0, 0];
  for (let c = 0; c < 3; c++) {
    const histogram = new Uint32Array(256);
    for (const i of indices) histogram[data[i * 4 + c]]++;
    let seen = 0;
    for (let v = 0; v < 256; v++) {
      seen += histogram[v];
      if (seen * 2 >= indices.length) {
        colour[c] = v;
        break;
      }
    }
  }
  return colour;
};


// How much a row varies around its own median colour. Joint rows are nearly uniform;
// brick rows vary from brick to brick and are interrupted by vertical joints, whatever
// the brick and joint colours are.
const rowSpreads = (image: Pixels) => {
  const { data, width, height } = image;
  const spreads = new Float32Array(height);
  const row: number[] = [];
  for (let y = 0; y < height; y++) {
    row.length = 0;
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      if (isOpaque(data, i)) row.push(i);
    }
    if (row.length === 0) {
      spreads[y] = NaN;
      continue;
    }
    const centre = medianColour(data, row);
    let sum = 0;
    for (const i of row) sum += distance(data, i, centre);
    spreads[y] = sum / row.length;
  }
  return spreads;
};

const runs = (profile: Float32Array, inRun: (value: number) => boolean): Span[] => {
  const result: Span[] = [];
  let start = -1;
  for (let i = 0; i <= profile.length; i++) {
    const inside = i < profile.length && !Number.isNaN(profile[i]) && inRun(profile[i]);
    if (inside && start < 0) start = i;
    if (!inside && start >= 0) {
      result.push({ start, end: i });
      start = -1;
    }
  }
  return result;
};

const gaps = (spans: Span[], length: number): Span[] => {
  const result: Span[] = [];
  let start = 0;
  for (const span of spans) {
    if (span.start > start) result.push({ start, end: span.start });
    start = span.end;
  }
  if (start < length) result.push({ start, end: length });
  return result;
};

const jointColourOf = (image: Pixels, jointRows: Span[]) => {
  const { data, width } = image;
  const pixels: number[] = [];
  for (const { start, end } of jointRows) {
    for (let y = start; y < end; y++) {
      for (let x = 0; x < width; x++) {
        const i = y * width + x;
        if (isOpaque(data, i)) pixels.push(i);
      }
    }
  }
  const step = Math.max(1, Math.floor(pixels.length / MAX_JOINT_SAMPLES));
  const sample = pixels.filter((_, k) => k % step === 0);
  const colour = medianColour(data, sample);
  const distances = sample.map((i) => distance(data, i, colour)).sort((a, b) => a - b);
  const spread = distances[Math.floor(distances.length * JOINT_SPREAD_PERCENTILE)] ?? 0;
  return { colour, tolerance: Math.max(MIN_JOINT_TOLERANCE, JOINT_TOLERANCE_FACTOR * spread) };
};

// Per column of a course: the share of joint-coloured pixels, and how close the column
// is to the joint colour on average (used to split bricks whose joint barely shows).
const columnProfiles = (image: Pixels, y0: number, y1: number, joint: Colour, tolerance: number) => {
  const { data, width } = image;
  const fill = new Float32Array(width);
  const closeness = new Float32Array(width);
  for (let x = 0; x < width; x++) {
    let jointPixels = 0;
    let total = 0;
    let sum = 0;
    for (let y = y0; y < y1; y++) {
      const i = y * width + x;
      if (!isOpaque(data, i)) continue;
      const d = distance(data, i, joint);
      if (d <= tolerance) jointPixels++;
      sum += d;
      total++;
    }
    fill[x] = total ? jointPixels / total : NaN;
    closeness[x] = total ? -sum / total : NaN;
  }
  return { fill, closeness };
};

const split = (segment: Span, closeness: Float32Array, fullWidth: number): Span[] => {
  if (segment.end - segment.start <= SPLIT_RATIO * fullWidth) return [segment];
  const lo = Math.ceil(segment.start + SPLIT_EDGE_RATIO * fullWidth);
  const hi = Math.floor(segment.end - SPLIT_EDGE_RATIO * fullWidth);
  if (hi <= lo) return [segment];
  let best = lo;
  for (let x = lo; x < hi; x++) if (closeness[x] > closeness[best]) best = x;
  return [
    ...split({ start: segment.start, end: best }, closeness, fullWidth),
    ...split({ start: best + 1, end: segment.end }, closeness, fullWidth),
  ];
};

const hasTransparentCorner = (image: Pixels, columns: Span, rows: Span) =>
  [
    [columns.start, rows.start],
    [columns.end - 1, rows.start],
    [columns.start, rows.end - 1],
    [columns.end - 1, rows.end - 1],
  ].some(([x, y]) => !isOpaque(image.data, y * image.width + x));

export const detectBricks = ({ image, brickWidthMm, brickHeightMm }: DetectionInput): DetectionResult => {
  const { width, height } = image;

  const spreads = rowSpreads(image);
  const values = finiteValues(spreads);
  if (values.length === 0) return EMPTY;
  // Split on the logarithm: rows fall into joint, brick interior and brick edge (ragged or
  // shaded) groups, and a linear split separates the edges from the rest when joints are thin.
  const threshold = Math.exp(otsu(values.map((v) => Math.log(Math.max(v, 1)))));
  const low = values.filter((v) => v <= threshold);
  const high = values.filter((v) => v > threshold);
  if (low.length === 0 || high.length === 0) return EMPTY;
  if (mean(high) < MIN_ROW_CONTRAST * Math.max(mean(low), 1)) return EMPTY;

  const jointRows = runs(spreads, (v) => v <= threshold);
  const courses = gaps(jointRows, height).filter(
    (c) => c.start > 0 && c.end < height && c.end - c.start >= 3
  );
  if (courses.length === 0) return EMPTY;

  const joint = jointColourOf(image, jointRows);
  const pxPerMm = median(courses.map((c) => c.end - c.start)) / brickHeightMm;
  const fullWidth = brickWidthMm * pxPerMm;
  const margin = Math.max(1, Math.round(MARGIN_MM * pxPerMm));

  const regions: DetectedRegion[] = [];
  for (const course of courses) {
    const inset = Math.floor((course.end - course.start) * COURSE_INSET);
    const { fill, closeness } = columnProfiles(
      image,
      course.start + inset,
      course.end - inset,
      joint.colour,
      joint.tolerance
    );
    const segments = gaps(runs(fill, (v) => v > JOINT_COLUMN_FILL), width)
      .filter((s) => s.start > 0 && s.end < width)
      .flatMap((s) => split(s, closeness, fullWidth))
      .filter((s) => s.end - s.start >= MIN_WIDTH_RATIO * fullWidth);
    for (const segment of segments) {
      if (hasTransparentCorner(image, segment, course)) continue;
      const x = Math.max(0, segment.start - margin);
      const y = Math.max(0, course.start - margin);
      regions.push({
        x,
        y,
        width: Math.min(width, segment.end + margin) - x,
        height: Math.min(height, course.end + margin) - y,
        type: segment.end - segment.start < HALF_BRICK_RATIO * fullWidth ? "half" : "full",
      });
    }
  }
  return { pxPerMm, regions };
};
